import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: '1. Información que Recopilamos',
    text: 'Recopilamos información personal que usted nos proporciona directamente al realizar una compra, como su nombre, dirección de correo electrónico e información de pago. No almacenamos datos de tarjetas de crédito; estos son procesados de forma segura por nuestro proveedor de pagos.'
  }, {
    heading: '2. Uso de la Información',
    text: 'Utilizamos su información personal para: procesar su compra y entregar el producto digital, enviar confirmaciones de pedido y acceso al material, comunicarnos con usted sobre actualizaciones del producto, y mejorar nuestros servicios y experiencia del usuario.'
  }, {
    heading: '3. Protección de Datos',
    text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger su información personal contra acceso no autorizado, alteración, divulgación o destrucción. Utilizamos cifrado SSL para todas las transacciones.'
  }, {
    heading: '4. Compartir Información',
    text: 'No vendemos, comercializamos ni transferimos su información personal a terceros. Esto no incluye terceros de confianza que nos ayudan a operar nuestro sitio web o a realizar nuestro negocio, siempre que dichas partes acuerden mantener esta información confidencial.'
  }, {
    heading: '5. Cookies',
    text: 'Nuestro sitio puede utilizar cookies para mejorar la experiencia del usuario. Puede configurar su navegador para rechazar cookies, aunque esto puede afectar algunas funcionalidades del sitio.'
  }, {
    heading: '6. Contacto',
    text: 'Si tiene preguntas sobre esta política de privacidad, puede contactarnos a través de nuestro correo electrónico de soporte.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: '1. Aceptación de los Términos',
    text: 'Al acceder y realizar una compra en este sitio, usted acepta estar sujeto a estos términos y condiciones de uso. Si no está de acuerdo con alguna parte de estos términos, no debe utilizar nuestro sitio ni adquirir nuestros productos.'
  }, {
    heading: '2. Producto Digital',
    text: 'El "Protocolo SOS Nervio Ciático" es un producto digital de carácter informativo y educativo. No constituye consejo médico profesional, diagnóstico ni tratamiento. Siempre consulte a un profesional de la salud calificado antes de comenzar cualquier programa de ejercicios o tratamiento.'
  }, {
    heading: '3. Entrega del Producto',
    text: 'Al completar su compra, recibirá acceso inmediato al producto digital a través del correo electrónico proporcionado. Es su responsabilidad asegurarse de que la dirección de correo electrónico sea correcta.'
  }, {
    heading: '4. Política de Reembolso',
    text: 'Debido a la naturaleza digital del producto y su entrega inmediata, todas las ventas son finales. Sin embargo, si experimenta problemas técnicos para acceder al material, nuestro equipo de soporte le asistirá para resolver cualquier inconveniente.'
  }, {
    heading: '5. Propiedad Intelectual',
    text: 'Todo el contenido del producto, incluyendo textos, imágenes, gráficos y materiales educativos, está protegido por derechos de autor. Queda prohibida la reproducción, distribución o reventa del material sin autorización expresa por escrito.'
  }, {
    heading: '6. Limitación de Responsabilidad',
    text: 'Los resultados pueden variar de persona a persona. No garantizamos resultados específicos. El contenido es informativo y no reemplaza la consulta médica profesional. No nos hacemos responsables por lesiones o daños derivados del uso incorrecto de las técnicas descritas.'
  }, {
    heading: '7. Modificaciones',
    text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en el sitio.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const content = activeModal === 'privacy' ? privacyContent : termsContent;
  return <>
      <footer className="bg-charcoal border-t border-white/10 py-8 px-4" data-id="element-44">
        <div className="max-w-3xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-45">
          <p className="text-white/40 text-sm font-body" data-id="element-46">
            © {new Date().getFullYear()} Protocolo SOS Nervio Ciático. Todos
            los derechos reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-47">
            <button onClick={() => setActiveModal('privacy')} className="text-white/50 hover:text-white text-sm font-body transition-colors focus:outline-none focus:ring-2 focus:ring-orange/50 rounded" data-id="element-48">
              Política de Privacidad
            </button>
            <span className="text-white/20" data-id="element-49">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-white/50 hover:text-white text-sm font-body transition-colors focus:outline-none focus:ring-2 focus:ring-orange/50 rounded" data-id="element-50">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-51">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 pt-12 md:pt-20 overflow-y-auto" onClick={() => setActiveModal(null)} data-id="element-52">
            <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.3
        }} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mb-12 relative" onClick={e => e.stopPropagation()} data-id="element-53">
              {/* Header */}
              <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-100 px-6 md:px-8 py-5 flex items-center justify-between z-10" data-id="element-54">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-charcoal" data-id="element-55">
                  {content.title}
                </h2>
                <button onClick={() => setActiveModal(null)} className="p-2 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-orange/50" aria-label="Cerrar" data-id="element-56">
                  <X className="w-5 h-5 text-warm-gray" data-id="element-57" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 md:px-8 py-6 space-y-6" data-id="element-58">
                {content.sections.map((section, index) => <div key={index} data-id="element-59">
                    <h3 className="font-heading font-bold text-lg text-charcoal mb-2" data-id="element-60">
                      {section.heading}
                    </h3>
                    <p className="font-body text-warm-gray leading-[1.7]" data-id="element-61">
                      {section.text}
                    </p>
                  </div>)}

                <div className="pt-4 border-t border-gray-100" data-id="element-62">
                  <p className="font-body text-sm text-warm-gray/70" data-id="element-63">
                    Última actualización: Marzo 2026
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}